import React from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { Edit, Delete, Add, ExpandMore, Search, Close, CategoryOutlined, RestaurantMenuOutlined } from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ItemRow from './ItemRow';
import { getNome, menuPalette } from '../utils/dashboardHelpers';
import { buildDeleteDialogMessage } from './DeleteDialogMessage';
import { deleteCardapio, deleteCategoria, deleteItem } from '../../../../services/api';

const MenuTreePanel = ({
  filteredCardapios,
  filterText,
  setFilterText,
  openForm,
  openAvailability,
  onDragEnd,
  reorderMode,
  expandedCardapioId,
  setExpandedCardapioId,
  selectedChannelMeta,
  handleDelete,
  toggleItemActive,
}) => (
  <>
    {(filteredCardapios || []).length === 0 && (
      <Paper
        sx={{
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          border: `1.5px dashed ${menuPalette.border}`,
          background: 'rgba(255,255,255,0.85)',
        }}
      >
        {filterText ? (
          <>
            <Search sx={{ fontSize: '2.4rem', color: menuPalette.blue, opacity: 0.7, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: menuPalette.ink, mb: 0.5 }}>
              Nenhum resultado para “{filterText}”
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tente outro termo ou limpe a busca para ver todos os cardápios.
            </Typography>
            <Button variant="outlined" startIcon={<Close />} onClick={() => setFilterText('')}>
              Limpar busca
            </Button>
          </>
        ) : (
          <>
            <RestaurantMenuOutlined sx={{ fontSize: '2.4rem', color: menuPalette.blue, opacity: 0.7, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: menuPalette.ink, mb: 0.5 }}>
              Você ainda não tem cardápios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crie seu primeiro cardápio para começar a montar a sua carta.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => openForm('cardapio')}>
              Novo cardápio
            </Button>
          </>
        )}
      </Paper>
    )}
        {/* DragDropContext de Cardápios */}
        <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="cardapios" type="CARDAPIO">
        {(provided) => (
          <Box ref={provided.innerRef} {...provided.droppableProps}>
            {(filteredCardapios || []).map((cardapio, indexCard) => (
              <Draggable
                key={`cardapio-${cardapio.id}`}
                draggableId={`cardapio-${cardapio.id}`}
                index={indexCard}
                isDragDisabled={!reorderMode || !!filterText}
              >
                {(dragProvided) => (
                  <Paper
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    sx={{
                      mb: 3,
                      p: { xs: 1.5, md: 2.25 },
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,250,242,0.96))',
                      border: `1.5px solid ${menuPalette.blue}`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        inset: 12,
                        border: `1px dashed ${menuPalette.border}`,
                        borderRadius: 2,
                        opacity: 0.4,
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                      sx={{ position: 'relative', zIndex: 1, cursor: 'pointer' }}
                      onClick={() =>
                        setExpandedCardapioId((prev) => (prev === cardapio.id ? null : cardapio.id))
                      }
                    >
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {reorderMode && !filterText && (
                        <Box {...dragProvided.dragHandleProps} sx={{ color: 'text.disabled' }}>
                          <DragIndicatorIcon />
                        </Box>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          mb: -0.1,
                          fontSize: '0.66rem',
                          fontWeight: 800,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: 'rgba(31,79,191,0.72)',
                          lineHeight: 1,
                        }}
                      >
                        Cardápio
                      </Typography>
                      <Typography variant="h6" sx={{ letterSpacing: '0.06em', textTransform: 'uppercase', color: menuPalette.blue }}>
                        {getNome(cardapio) || 'Cardápio sem nome'}
                      </Typography>
                      {reorderMode && !filterText && (
                        <Chip size="small" label={`ordem: ${indexCard + 1}`} sx={{ fontWeight: 700 }} />
                      )}
                      <Chip
                        size="small"
                        label={cardapio.ativo ? `Ativo no ${selectedChannelMeta.label}` : `Inativo no ${selectedChannelMeta.label}`}
                        color={cardapio.ativo ? 'success' : 'default'}
                        variant={cardapio.ativo ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 700, letterSpacing: '0.05em' }}
                      />
                      {(() => {
                        const catCount = cardapio.categorias?.length || 0;
                        const itemCount = (cardapio.categorias || []).reduce((acc, c) => acc + (c.itens?.length || 0), 0);
                        return (
                          <Tooltip title={`${catCount} categoria(s) e ${itemCount} item(ns) neste cardápio`}>
                            <Chip
                              size="small"
                              icon={<CategoryOutlined sx={{ fontSize: '0.95rem !important' }} />}
                              label={`${catCount} cat · ${itemCount} itens`}
                              variant="outlined"
                              sx={{
                                fontWeight: 700,
                                color: menuPalette.blue,
                                borderColor: menuPalette.border,
                                backgroundColor: 'rgba(31,79,191,0.05)',
                                '& .MuiChip-icon': { color: menuPalette.blue },
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                      <ExpandMore
                        sx={{
                          color: menuPalette.blue,
                          transform: expandedCardapioId === cardapio.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                        }}
                      />
                    </Box>
                      {!reorderMode && (
                        <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openAvailability(cardapio)}
                          >
                            Horários
                          </Button>
                          <Button size="small" startIcon={<Add />} onClick={() => openForm('categoria', {}, { cardapio_id: cardapio.id })} color="secondary" variant="outlined" sx={{ borderWidth: 2 }}>
                            Categoria
                          </Button>
                          <IconButton size="small" onClick={() => openForm('cardapio', cardapio)} aria-label="Editar Cardápio"><Edit fontSize="small" /></IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(deleteCardapio, cardapio.id, {
                              title: 'Excluir cardápio',
                              message: buildDeleteDialogMessage('Ao excluir este cardápio, todas as categorias e todos os itens vinculados a ele também serão excluídos automaticamente.'),
                              confirmText: 'Excluir cardápio',
                            })}
                            aria-label="Excluir Cardápio"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                    {expandedCardapioId === cardapio.id && (
                      <>
                        <Divider sx={{ borderColor: menuPalette.border, mb: 1 }} />

                        <Droppable droppableId={`categorias-${cardapio.id}`} type="CATEGORIA" isDropDisabled={!reorderMode || !!filterText}>
                      {(catsProvided) => (
                        <Box ref={catsProvided.innerRef} {...catsProvided.droppableProps}>
                          {(cardapio.categorias || [])
                            .sort((a,b)=>(a.ordem??0)-(b.ordem??0))
                            .map((categoria, indexCat) => (
                            <Draggable
                              key={`categoria-${categoria.id}`}
                              draggableId={`categoria-${categoria.id}`}
                              index={indexCat}
                              isDragDisabled={!reorderMode || !!filterText}
                            >
                              {(catDragProvided) => (
                                <Accordion
                                  defaultExpanded
                                  ref={catDragProvided.innerRef}
                                  {...catDragProvided.draggableProps}
                                  sx={{
                                    mt: 1.5,
                                    boxShadow: 'none',
                                    overflow: 'hidden',
                                    '&:before': { display: 'none' },
                                    border: `1px solid ${menuPalette.categoriaBorder}`,
                                    borderRadius: 2,
                                    background: menuPalette.categoriaPanelBg,
                                    '&:hover': { boxShadow: '0 12px 24px rgba(27,43,82,0.12)' },
                                  }}
                                >
                                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ background: menuPalette.categoriaSummaryBg }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={1.5}>
                                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                        {reorderMode && !filterText && (
                                          <Box {...catDragProvided.dragHandleProps} sx={{ color: 'text.disabled' }}>
                                            <DragIndicatorIcon fontSize="small" />
                                          </Box>
                                        )}
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            width: '100%',
                                            mb: -0.1,
                                            fontSize: '0.66rem',
                                            fontWeight: 800,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: 'rgba(242,107,58,0.82)',
                                            lineHeight: 1,
                                          }}
                                        >
                                          Categoria
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700, letterSpacing: '0.04em', color: menuPalette.ink }}>
                                          {getNome(categoria) || 'Categoria sem nome'}
                                        </Typography>
                                        {reorderMode && !filterText && <Chip size="small" label={`ordem: ${indexCat + 1}`} sx={{ fontWeight: 700 }} />}
                                        <Chip
                                          size="small"
                                          label={categoria.ativo ? 'Ativa' : 'Inativa'}
                                          color={categoria.ativo ? 'success' : 'default'}
                                          variant={categoria.ativo ? 'outlined' : 'filled'}
                                          sx={{ fontWeight: 700, letterSpacing: '0.05em' }}
                                        />
                                        <Tooltip title={`${categoria.itens?.length || 0} item(ns) nesta categoria`}>
                                          <Chip
                                            size="small"
                                            icon={<RestaurantMenuOutlined sx={{ fontSize: '0.9rem !important' }} />}
                                            label={`${categoria.itens?.length || 0} itens`}
                                            variant="outlined"
                                            sx={{
                                              fontWeight: 700,
                                              color: menuPalette.orange,
                                              borderColor: menuPalette.categoriaBorder,
                                              backgroundColor: 'rgba(242,107,58,0.06)',
                                              '& .MuiChip-icon': { color: menuPalette.orange },
                                            }}
                                          />
                                        </Tooltip>
                                      </Box>
                                      {!reorderMode && (
                                        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                          <IconButton size="small" onClick={() => openForm('categoria', categoria, { cardapio_id: cardapio.id })} aria-label="Editar Categoria"><Edit fontSize="small" /></IconButton>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(deleteCategoria, categoria.id, {
                                              title: 'Excluir categoria',
                                              message: buildDeleteDialogMessage('Ao excluir esta categoria, todos os itens vinculados a ela também serão excluídos automaticamente.'),
                                              confirmText: 'Excluir categoria',
                                            })}
                                            aria-label="Excluir Categoria"
                                          >
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Stack>
                                      )}
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ p: 0, overflow: 'hidden', backgroundColor: menuPalette.categoriaDetailsBg, borderTop: `1px dashed ${menuPalette.categoriaBorder}` }}>
                                    <Droppable droppableId={`itens-${categoria.id}`} type="ITEM" isDropDisabled={!reorderMode || !!filterText}>
                                      {(itProv) => (
                                        <Box ref={itProv.innerRef} {...itProv.droppableProps}>
                                          {(categoria.itens || [])
                                            .sort((a,b)=>(a.ordem??0)-(b.ordem??0))
                                            .map((item, indexItem) => (
                                            <Draggable
                                              key={`item-${item.id}`}
                                              draggableId={`item-${item.id}`}
                                              index={indexItem}
                                              isDragDisabled={!reorderMode || !!filterText}
                                            >
                                              {(itDrag) => (
                                                <div
                                                  ref={itDrag.innerRef}
                                                  {...itDrag.draggableProps}
                                                  {...(!filterText && reorderMode ? itDrag.dragHandleProps : {})}
                                                >
                                                  <ItemRow
                                                    item={item}
                                                    onEdit={() => openForm('item', item, { categoria_id: categoria.id, cardapio_id: cardapio.id })}
                                                    onDelete={() => handleDelete(() => deleteItem(item.id, categoria.id), item.id, {
                                                      title: 'Excluir item',
                                                      message: buildDeleteDialogMessage('Ao excluir este item, ele será removido permanentemente deste cardápio.'),
                                                      confirmText: 'Excluir item',
                                                    })}
                                                    showHandle={reorderMode && !filterText}
                                                    onToggleActive={(checked) => toggleItemActive(categoria.id, item, checked)}
                                                    channelLabel={selectedChannelMeta.label}
                                                  />
                                                </div>
                                              )}
                                            </Draggable>
                                          ))}
                                          {itProv.placeholder}
                                          {!reorderMode && (categoria.itens || []).length === 0 && (
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                py: 2.5,
                                                px: 2,
                                                color: 'text.secondary',
                                              }}
                                            >
                                              <RestaurantMenuOutlined sx={{ color: menuPalette.categoriaBorder, fontSize: '1.6rem' }} />
                                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                Nenhum item nesta categoria ainda
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                                Use o botão abaixo para adicionar o primeiro item.
                                              </Typography>
                                            </Box>
                                          )}
                                          {!reorderMode && (
                                            <Box p={1.25}>
                                              <Button
                                                fullWidth
                                                startIcon={<Add />}
                                                onClick={() => openForm('item', {}, { categoria_id: categoria.id, cardapio_id: cardapio.id })}
                                                sx={{
                                                  borderRadius: 2,
                                                  border: `1.5px dashed ${menuPalette.categoriaBorder}`,
                                                  color: menuPalette.orange,
                                                  fontWeight: 800,
                                                  py: 1,
                                                  backgroundColor: 'rgba(242,107,58,0.04)',
                                                  boxShadow: 'none',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(242,107,58,0.1)',
                                                    borderStyle: 'solid',
                                                    boxShadow: 'none',
                                                  },
                                                }}
                                              >
                                                Adicionar Item
                                              </Button>
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </Droppable>
                                  </AccordionDetails>
                                </Accordion>
                              )}
                            </Draggable>
                          ))}
                          {catsProvided.placeholder}
                        </Box>
                      )}
                        </Droppable>
                      </>
                    )}
                  </Paper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
        </Droppable>
        </DragDropContext>
        </>

);

export default MenuTreePanel;
